<script setup lang="ts">
import type { TimeRange, WaveformData, ExportFormat, EditMode } from '@/types'
import WaveformEditor from '@/components/WaveformEditor.vue'
import PlaybackControls from '@/components/PlaybackControls.vue'

const props = defineProps<{
  waveformData: WaveformData | null
  duration: number
  selection: TimeRange
  currentTime: number
  filename?: string
  isPlaying: boolean
  loop: boolean
  isExporting: boolean
  exportProgress: number
  editMode: EditMode
}>()

const emit = defineEmits<{
  'update:selection': [selection: TimeRange]
  'seek': [time: number]
  'toggle-play': []
  'replay': []
  'toggle-loop': []
  'toggle-mode': []
  'export': [format: ExportFormat]
  'cancel-export': []
  'reset': []
}>()

const handleSelectionChange = (newSelection: TimeRange) => {
  emit('update:selection', newSelection)
}

const handleSeek = (time: number) => {
  emit('seek', time)
}
</script>

<template>
  <div class="editor">
    <div class="waveform-section card">
      <!-- Waveform -->
      <WaveformEditor
        :waveform-data="waveformData"
        :duration="duration"
        :selection="selection"
        :current-time="currentTime"
        :filename="filename"
        :edit-mode="props.editMode"
        @update:selection="handleSelectionChange"
        @seek="handleSeek"
      />

      <!-- Playback Controls -->
      <PlaybackControls
        :is-playing="isPlaying"
        :loop="loop"
        :current-time="currentTime"
        :selection="selection"
        :duration="duration"
        :is-exporting="isExporting"
        :export-progress="exportProgress"
        :edit-mode="props.editMode"
        @toggle-play="emit('toggle-play')"
        @replay="emit('replay')"
        @toggle-loop="emit('toggle-loop')"
        @toggle-mode="emit('toggle-mode')"
        @seek="handleSeek"
        @export="(format) => emit('export', format)"
        @cancel-export="emit('cancel-export')"
        @reset="emit('reset')"
      />
    </div>
  </div>
</template>

<style scoped>
.editor {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.waveform-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  /* Match FileUpload height minus padding to prevent CLS */
  min-height: 291px;
  box-shadow: var(--shadow-md);
  /* CSS containment for better performance */
  contain: layout style;
}

@media (max-width: 640px) {
  .waveform-section {
    /* Match FileUpload mobile height minus padding */
    min-height: 232px;
  }
}

/* Remove card hover effect for the editor section */
.waveform-section:hover {
  box-shadow: var(--shadow-md);
}
</style>
